import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import ChevLeft12 from '@alltrails/shared/components/ChevLeft12';
import ChevRight12 from '@alltrails/shared/components/ChevRight12';
import FilterPillFactory from 'components/explore/filters/FilterPillFactory';
import NestedPanel from 'components/explore/filters/NestedPanel';
import { SearchResultsCountFunction } from 'hooks/useHypotheticalSearchResultsCount';
import { Context } from 'types/Context';
import { Filter, FilterRangeUpdates } from 'types/Search';
import { StringConstantsMixin } from '../../../application/javascripts/react_components/mixins/shared/string_constants_mixin';
import * as styles from './styles/styles.module.scss';

const ScrollButtonIncrementPx = 50;

export type Props = {
  clearRadioFilter?: (filterKey: string, pageName: string) => void;
  context: Context;
  enabledFilters: string[];
  handleFilterRadioToggle?: (filterKey: string, filterConfigKey: string) => void;
  handleFilterSet?: (filterKey: string, value: string) => void;
  handleFilterSlider?: (filterKey: string, value: FilterRangeUpdates) => void;
  handleFilterToggle?: (filterKey: string, filterConfigKey: string) => void;
  page: string;
  resultsCount: number;
  searchResultsCountFunction: SearchResultsCountFunction;
  sortedFilters: Filter[];
};

const FilterBar = ({
  clearRadioFilter,
  context,
  enabledFilters,
  handleFilterRadioToggle,
  handleFilterSet,
  handleFilterSlider,
  handleFilterToggle,
  page,
  resultsCount,
  searchResultsCountFunction,
  sortedFilters
}: Props) => {
  const scrollContainerRef = useRef<HTMLDivElement>();
  const [isScrollable, setIsScrollable] = useState(false);

  const reducedFilters = sortedFilters.reduce(
    (memo, nextFilter) => {
      if (!enabledFilters.includes(nextFilter.enabledKey)) {
        return memo;
      }

      // The admin modal and nested modal filters are mutually exclusive.
      if (nextFilter.admin) {
        // It's an admin filter, but only render it if it has data.
        if (Object.keys(nextFilter.config).length) {
          memo.adminFilters.push(nextFilter);
        }
      } else if (nextFilter.nested) {
        memo.moreFilters.push(nextFilter);
      } else {
        // Everything else is a top-level filter.
        memo.standardFilters.push(nextFilter);
      }

      return memo;
    },
    { adminFilters: [], moreFilters: [], standardFilters: [] }
  );

  const updateIsScrollable = () => {
    if (!scrollContainerRef.current) {
      return;
    }

    const parentWidth = scrollContainerRef.current.parentElement.getBoundingClientRect().width;
    const { scrollWidth } = scrollContainerRef.current;

    setIsScrollable(scrollWidth > parentWidth);
  };

  useEffect(() => {
    window.addEventListener('resize', updateIsScrollable);

    updateIsScrollable();

    return () => {
      window.removeEventListener('resize', updateIsScrollable);
    };
  }, []);

  const scroll = (scrollIncrement: number) => {
    if (!scrollContainerRef.current) {
      return;
    }

    scrollContainerRef.current.scrollBy(scrollIncrement, 0);
  };

  return (
    <div className={styles.filterBarWrapper}>
      <div className={styles.filterBar} ref={scrollContainerRef}>
        {reducedFilters.standardFilters.map((filter: Filter) => (
          <div className={styles.inlinePill} key={filter.key}>
            <FilterPillFactory
              clearRadioFilter={clearRadioFilter}
              filter={filter}
              handleFilterRadioToggle={handleFilterRadioToggle}
              handleFilterSet={handleFilterSet}
              handleFilterSlider={handleFilterSlider}
              handleFilterToggle={handleFilterToggle}
              page={page}
              popperClassName={styles.popper}
              resultsCount={resultsCount}
              searchResultsCountFunction={searchResultsCountFunction}
            />
          </div>
        ))}
        {reducedFilters.moreFilters.length > 1 && (
          <div className={styles.inlinePill}>
            <NestedPanel
              clearRadioFilter={clearRadioFilter}
              handleFilterRadioToggle={handleFilterRadioToggle}
              handleFilterSet={handleFilterSet}
              handleFilterSlider={handleFilterSlider}
              handleFilterToggle={handleFilterToggle}
              page={page}
              popperClassName={styles.popper}
              resultsCount={resultsCount}
              searchResultsCountFunction={searchResultsCountFunction}
              sortedFilters={reducedFilters.moreFilters}
              title={StringConstantsMixin.strings.FILTERS.MORE_FILTERS}
            />
          </div>
        )}
        {reducedFilters.adminFilters.length > 0 && context?.adminUser && (
          <div className={styles.inlinePill}>
            <NestedPanel
              clearRadioFilter={clearRadioFilter}
              sortedFilters={reducedFilters.adminFilters}
              handleFilterRadioToggle={handleFilterRadioToggle}
              handleFilterSet={handleFilterSet}
              handleFilterSlider={handleFilterSlider}
              handleFilterToggle={handleFilterToggle}
              page={page}
              popperClassName={styles.popper}
              resultsCount={resultsCount}
              searchResultsCountFunction={searchResultsCountFunction}
              title="Admin"
            />
          </div>
        )}
      </div>
      {isScrollable && (
        <div className={styles.scrollControls}>
          <button type="button" onClick={() => scroll(-ScrollButtonIncrementPx)}>
            <span className={styles.icon}>
              <ChevLeft12 />
            </span>
            <span className={classNames(styles.gradient, styles.startGradient)} />
          </button>
          <button type="button" onClick={() => scroll(ScrollButtonIncrementPx)}>
            <span className={classNames(styles.gradient, styles.endGradient)} />
            <span className={styles.icon}>
              <ChevRight12 />
            </span>
          </button>
        </div>
      )}
    </div>
  );
};

export default FilterBar;
